import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { cloneDeep } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { DragLayerMonitor, useDrag, useDragLayer, useDrop, XYCoord } from "react-dnd";
import { useDndDataTableContext } from "./DndDataTableContext";
import "./movableDataTableRow.scss";

interface IMovableDataTableRowProps<Row> {
  id: number;
  position: number;
  className?: string;
  onClick?: () => void;
  rows: Row[];
  setRows: (setState: (originalRows: Row[]) => Row[]) => void;
  children: React.ReactNode;
}

export interface IMovableDataTableRowItem {
  id: number;
}

export const MovableDataTableBodyItemTypes = {
  ROW: "row",
};

interface IRowNecessaryFields {
  id: number;
  position: number;
}

export default function MovableDataTableRow<Row extends IRowNecessaryFields>(props: IMovableDataTableRowProps<Row>) {
  const { id, position, className, onClick, rows, setRows, children } = props;

  const rowRef = useRef<HTMLDivElement>(null);

  const [dropHint, setDropHint] = useState<"top" | "bottom" | "none">("none");

  const dndDataTableContext = useDndDataTableContext();

  const [{ isOver, dropId }, drop] = useDrop(
    () => ({
      accept: MovableDataTableBodyItemTypes.ROW,
      canDrop: (item: IMovableDataTableRowItem, monitor) => {
        return item.id !== id && dndDataTableContext.enableDragAndDrop;
      },
      drop: (item: IMovableDataTableRowItem, monitor) => {
        const rowBoundingRectangle = rowRef.current.getBoundingClientRect();
        const clientOffset = monitor.getClientOffset();

        const dropPosition = {
          x: clientOffset.x - rowBoundingRectangle.x,
          y: clientOffset.y - rowBoundingRectangle.y,
        };

        if (dropPosition.y <= rowBoundingRectangle.height / 2) {
          moveRow(item.id, position, "before");
        } else {
          moveRow(item.id, position, "after");
        }
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        dropId: monitor.getItem()?.id,
      }),
    }),
    [rowRef?.current, position],
  );

  const [{ isDragging }, drag, dragPreview] = useDrag(() => {
    const item: IMovableDataTableRowItem = {
      id,
    };

    return {
      item,
      type: MovableDataTableBodyItemTypes.ROW,
      canDrag: dndDataTableContext.enableDragAndDrop,
      collect: monitor => ({
        isDragging: !!monitor.isDragging(),
      }),
    };
  }, [id]);

  const { clientOffset } = useDragLayer((monitor: DragLayerMonitor) => {
    return {
      clientOffset: monitor.getClientOffset(),
    };
  });

  function moveRow(id: number, moveToPosition: number, shift: "before" | "after") {
    setRows(prevRows => {
      const updatedRows: Row[] = [];
      let updatedPosition = 1;

      const rowToMove = prevRows.find(row => row.id === id);

      if (!rowToMove || moveToPosition < 1 || moveToPosition > prevRows.length) {
        return prevRows;
      }

      for (let i = 0; i < prevRows.length; i++) {
        const currentRow = prevRows[i];

        if (currentRow.id === id) {
          continue;
        }

        if (i === moveToPosition - 1) {
          if (shift === "before") {
            updatedRows.push(
              { ...rowToMove, position: updatedPosition++ },
              { ...currentRow, position: updatedPosition++ },
            );
          } else if (shift === "after") {
            updatedRows.push(
              { ...currentRow, position: updatedPosition++ },
              { ...rowToMove, position: updatedPosition++ },
            );
          }
        } else {
          updatedRows.push({ ...currentRow, position: updatedPosition++ });
        }
      }

      return updatedRows;
    });
  }

  useEffect(() => {
    if (isOver && clientOffset?.y && id !== dropId) {
      const rowBoundingRectangle = rowRef.current.getBoundingClientRect();

      const relativeDragCoordinate: XYCoord = {
        x: clientOffset.x - rowBoundingRectangle.x,
        y: clientOffset.y - rowBoundingRectangle.y,
      };

      if (relativeDragCoordinate.y <= rowBoundingRectangle.height / 2) {
        setDropHint("top");
      } else {
        setDropHint("bottom");
      }
    } else {
      setDropHint("none");
    }
  }, [isOver, clientOffset?.y, id, dropId]);

  return (
    <tr
      ref={el => {
        drop(el);
        dragPreview(el);
        rowRef.current = el;
      }}
      className={classNames("", {
        [className]: className,
        "movable-data-table-row-drop-hint-top": dropHint === "top",
        "movable-data-table-row-drop-hint-bottom": dropHint === "bottom",
      })}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging && "grabbing",
      }}
      onClick={onClick}
    >
      {dndDataTableContext.enableDragAndDrop && (
        <td
          style={{
            width: "fit-content",
            padding: "0px",
          }}
        >
          <div className="movable-data-table-row-table-data-content">
            <div
              ref={drag}
              className="movable-data-table-row-grip-container"
              style={{
                cursor: isDragging ? "grabbing" : "grab",
              }}
              onClick={e => e.stopPropagation()}
            >
              <FontAwesomeIcon className="movable-data-table-row-grip" icon={["far", "grip-vertical"]} />
            </div>
          </div>
        </td>
      )}
      {children}
    </tr>
  );
}
